// src/portal-app/team/EmployeesScreen.js
//
// Plan 4 Task 9 — Employees screen, migrated to the P4 pattern (spec §5).
// See operations/TransactionsScreen.js:32-55 for the pattern (and its file
// header) this follows; more/ProductsScreen.js (Plan 4 Task 4) is the
// closest reviewed model — single fetch, client-side filter+search, FAB +
// create sheet, no per-row action button. This header only calls out
// decisions specific to Employees.
//
// AvatarRow — NOT used. The brief (and spec §5 P2) suggests it as "the
// fastest answer to who is working right now", but that requires an
// on-shift/clocked-in signal. GET /employees/v2/list -> _to_list_item()
// (backend/app/routers/employees_v2.py:82-103) returns only employee_id,
// name, role, department, status, a compensation snippet and
// access.is_cashier — no schedule/shift data at all. Populating AvatarRow
// would mean adding a second fetch (shiftService) purely to feed the hero —
// exactly the Task 6 regression the brief calls out and that was reverted
// there. The hero uses a plain headcount instead; secondary/on-shift is a
// count of active employees within the SAME filtered set, not "on shift".
//
// Hero denominator — filtered.length (search + role/department/status
// applied), the exact array rendered as rows below it, same rule Schedule
// (Task 8) followed. This is a deliberate change from the pre-migration
// stats bar, which always summarised the FULL unfiltered roster regardless
// of search/filter state — see "Dropped/changed fields" below.
//
// Filters — role, department and status all collapse into ONE FilterSheet
// (brief: "Filters into the sheet (role/department/status)"). Role and
// department options are derived from the currently loaded roster (like
// Transactions derives its staff/payment/dining chips from the response),
// not from a fixed taxonomy — a tenant with nobody in "Delivery" doesn't get
// an empty "Delivery" chip. Status is a fixed two-value domain (Active /
// Inactive), same values FilterRow used pre-migration, so it always renders.
// SegmentedControl was NOT considered: there is exactly one data source
// (employeeV2Service.listEmployees()) and all three axes are plain
// client-side filters over that one array — the Task 4 Products defect
// this rule guards against.
//
// Empty is genuine, even though filtering is client-side — same shape as
// Schedule (Task 8), not Products (Task 4) pre-fix: `status` itself can
// already be 'empty' (roster has zero employees), and separately
// `surfaceStatus` is DERIVED from the post-filter array
// (`status === 'ready' && filtered.length === 0 ? 'empty' : status`,
// unchanged from the pre-migration screen), so a filter/search that matches
// nobody genuinely flips SurfaceStates into its empty branch. See the task
// report's mutation-evidence section.
//
// Row — CondensedRow: icon is the employee's initials in their tone-coded
// circle (matches ProfileHero's own `{initials}` avatar exactly), NOT a
// generic SVG glyph — a person's identity IS the dominant fact of an
// employee row, more so than a payment method or a shift's time block, and
// this reuses the same tone-from-name formula EmployeeDetailScreen's hero
// uses (see avatarTone() below) so an employee's colour never changes
// between the list and the detail hero (Finding 7, pre-migration). Status is
// the row's pill — CondensedRow has no separate node slot for a StatusBadge
// element, the pill IS that slot (spec: colour is never the sole carrier of
// meaning, and doubly so for a person's employment status, not a product's —
// the pill always carries the status WORD, not colour alone).
//
// Dropped/changed fields (flagged per the task's Lesson 5):
//   - Pay (amount + frequency) is DROPPED from the row. The brief's field
//     list is explicit — "name, role · department as secondary, StatusBadge
//     for status" — and does not include it. Compensation is a sensitive
//     per-employee figure better suited to a deliberate look (row tap ->
//     EmployeeDetailScreen -> Payroll tab) than a number broadcast on every
//     scannable row. payDisplay()/fmtMoney (pre-migration helpers) are
//     removed as now-dead code.
//   - Cashier access (access.is_cashier) is COMPOSED into `secondary`
//     instead of dropped — pre-migration this was a second badge next to
//     the status pill, but CondensedRow has only one pill slot (reserved for
//     status per the brief). is_cashier is a distinct POS-access permission,
//     not implied by role text (a "Waiter" can also be a cashier), so
//     dropping it silently would be real information loss. Composed as
//     `"{role} · {department} · Cashier"` when true, omitted when false.
//   - Full unfiltered roster stats (previously always visible regardless of
//     search/filter) become the hero, which now tracks the FILTERED set
//     (see "Hero denominator" above) — this is a deliberate behaviour
//     change required by the P4 recipe's hero rule, not an oversight.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { employeeV2Service } from '../../services/api';
import { SurfaceStates } from '../insights/kit';
import {
    CondensedRow,
    SearchInput,
    FilterButton,
    FilterSheet,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
    FALLBACK_TONES,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { useNav, useNavChrome } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.emp-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 120px;}

/* ---- hero ---- */
.emp-hero{margin:0 16px 16px;padding:18px 20px 16px;}

/* ---- control row — FilterButton only (no RangeBar equivalent) ---- */
.emp-control-row{display:flex;justify-content:flex-end;padding:0 16px;
  margin-bottom:10px;}

/* ---- active-filter summary ---- */
.emp-filtersum{display:flex;align-items:center;gap:12px;
  margin:4px 16px 12px;}
.emp-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;}
.emp-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* ---- search ---- */
.emp-search-wrap{padding:0 16px;margin-bottom:10px;}

/* ---- count line + list ---- */
.emp-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}
.emp-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}

/* ---- FAB ---- */
.emp-fab{position:fixed;bottom:calc(80px + var(--safe-bottom,0px));
  inset-inline-end:20px;z-index:200;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  min-height:52px;min-width:52px;padding:0 20px;border-radius:26px;border:none;
  background:var(--c-green);
  font-family:inherit;font-size:14px;font-weight:700;color:var(--c-white);
  cursor:pointer;
  box-shadow:0 4px 20px color-mix(in srgb,var(--c-green) 40%,transparent);
  transition:transform .15s ease,box-shadow .15s ease;}
.emp-fab:active{transform:scale(.97);}

/* ---- Create sheet backdrop ---- */
.emp-sheet-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.emp-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:85vh;overflow-y:auto;}
.emp-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-bottom:16px;}
.emp-field{margin-bottom:14px;}
.emp-field-label{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.8px;color:var(--c-textLight);
  margin-bottom:5px;}
.emp-field-input{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;}
.emp-field-input:focus{border-color:var(--c-green);}
.emp-field-select{appearance:none;cursor:pointer;}
.emp-field-hint{margin-top:6px;font-size:12px;line-height:1.45;
  color:var(--c-textLight);}
.emp-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.emp-toggle-row{display:flex;align-items:center;justify-content:space-between;
  min-height:44px;padding:0 4px;}
.emp-toggle-label{font-size:14px;font-weight:600;color:var(--c-ink);}
.emp-toggle{position:relative;width:44px;height:26px;flex:none;}
.emp-toggle input{opacity:0;width:0;height:0;position:absolute;}
.emp-toggle-track{position:absolute;inset:0;border-radius:999px;
  background:color-mix(in srgb,var(--c-ink) 15%,transparent);
  transition:background .18s ease;}
.emp-toggle input:checked+.emp-toggle-track{background:var(--c-green);}
.emp-toggle-thumb{position:absolute;top:3px;left:3px;width:20px;height:20px;
  border-radius:50%;background:var(--c-white);
  transition:transform .18s ease;
  box-shadow:0 1px 4px rgba(0,0,0,.18);}
.emp-toggle input:checked~.emp-toggle-thumb{transform:translateX(18px);}
.emp-error{padding:10px 14px;border-radius:12px;margin-bottom:12px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 25%,transparent);
  font-size:13px;font-weight:600;color:var(--c-red);}
.emp-actions{display:flex;gap:10px;margin-top:6px;}
.emp-btn-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;
  transition:transform .15s ease;}
.emp-btn-cancel:active{transform:scale(.97);}
.emp-btn-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:transform .15s ease,opacity .15s ease;}
.emp-btn-save:active{transform:scale(.97);}
.emp-btn-save:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.emp-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:empSpin .75s linear infinite;}
@keyframes empSpin{to{transform:rotate(360deg);}}

/* ---- slide-up ---- */
@keyframes empSlideUp{
  from{transform:translateY(100%);opacity:0;}
  to{transform:translateY(0);opacity:1;}
}
.emp-sheet-enter{animation:empSlideUp 260ms ease-out both;}
@keyframes empFadeIn{from{opacity:0;}to{opacity:1;}}
.emp-backdrop-enter{animation:empFadeIn 200ms ease both;}

@media(prefers-reduced-motion:reduce){
  .emp-sheet-enter,.emp-backdrop-enter{animation:none;transition:none;}
  .emp-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'emp-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Identity helpers — initials + tone-from-name.                       */
/* Same charCode-modulo-FALLBACK_TONES formula EmployeeDetailScreen's   */
/* heroToneFromName uses, so an employee's colour never changes between */
/* this list and the detail hero (Finding 7, pre-migration). Kept as an */
/* independent copy (not a cross-screen import — EmployeesScreen has     */
/* never imported EmployeeDetailScreen, and the two screens are         */
/* reachable only through the nav stack, not a component import) and    */
/* proven identical by test instead — see EmployeesScreen.test.js.      */
/* ------------------------------------------------------------------ */
// Exported so EmployeesScreen.test.js can prove it's a pure function that
// always resolves within the shared palette — see the test file's comment
// for why this no longer cross-imports EmployeeDetailScreen.heroToneFromName
// directly.
export function avatarTone(name = '') {
    return FALLBACK_TONES[(name.charCodeAt(0) || 0) % FALLBACK_TONES.length];
}
const initials = (name = '') =>
    name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();

/* ------------------------------------------------------------------ */
/* Status pill tone                                                    */
/* ------------------------------------------------------------------ */
function statusTone(status) {
    return (status || '').toLowerCase() === 'active' ? 'green' : 'muted';
}

/* ------------------------------------------------------------------ */
/* SVG icons                                                          */
/* ------------------------------------------------------------------ */
const PlusIcon = () => (
    <svg
        width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true"
    >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Department / employment / pay constants (mirrors web page) — used   */
/* by the create-employee form only.                                   */
/* ------------------------------------------------------------------ */
const DEPARTMENT_OPTIONS = [
    { value: 'Kitchen', labelKey: 'dept_kitchen', fallback: 'Kitchen' },
    { value: 'Front of House', labelKey: 'dept_foh', fallback: 'Front of House' },
    { value: 'Bar', labelKey: 'dept_bar', fallback: 'Bar' },
    { value: 'Management', labelKey: 'dept_management', fallback: 'Management' },
    { value: 'Delivery', labelKey: 'dept_delivery', fallback: 'Delivery' },
    { value: 'Other', labelKey: 'dept_other', fallback: 'Other' },
];
const EMPLOYMENT_TYPES = [
    { value: 'full_time', labelKey: 'full_time_label', fallback: 'Full Time' },
    { value: 'part_time', labelKey: 'part_time_label', fallback: 'Part Time' },
    { value: 'contractor', labelKey: 'contractor', fallback: 'Contractor' },
];
const PAY_TYPES = [
    { value: 'salary', labelKey: 'salary', fallback: 'Salary' },
    { value: 'hourly', labelKey: 'hourly', fallback: 'Hourly' },
    { value: 'hybrid', labelKey: 'hybrid', fallback: 'Hybrid' },
];
const PAY_FREQUENCIES = [
    { value: 'daily', labelKey: 'pay_freq_daily', fallback: 'Daily' },
    { value: 'weekly', labelKey: 'pay_freq_weekly', fallback: 'Weekly' },
    { value: 'monthly', labelKey: 'pay_freq_monthly', fallback: 'Monthly' },
];

const BLANK_FORM = {
    name: '',
    role: '',
    department: '',
    employment_type: '',
    start_date: '',
    pay_type: 'salary',
    base_amount: '',
    pay_frequency: 'monthly',
    is_cashier: false,
    pin: '',
    system_role: 'staff',
    phone: '',
    email: '',
};

/* ------------------------------------------------------------------ */
/* CreateEmployeeSheet — unchanged behaviour/payload from pre-migration */
/* (same fields, same createEmployee() call). Only the unused `currency` */
/* prop (never referenced in the body) was dropped as dead code.        */
/* ------------------------------------------------------------------ */
function CreateEmployeeSheet({ open, onClose, onSaved, t }) {
    const [form, setForm] = useState(BLANK_FORM);
    const [saving, setSaving] = useState(false);
    const [localError, setLocalError] = useState('');
    const [serverError, setServerError] = useState('');

    useEffect(() => {
        if (!open) {
            setForm(BLANK_FORM);
            setLocalError('');
            setServerError('');
        }
    }, [open]);

    // Escape key dismissal
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setLocalError(t('name_is_required', {}, 'Name is required'));
            return;
        }
        setLocalError('');
        setServerError('');
        setSaving(true);

        const payload = {
            name: form.name.trim(),
            role: form.role.trim() || null,
            department: form.department || null,
            status: 'Active',
            personal: {
                phone: form.phone || null,
                email: form.email || null,
                preferred_language: 'en',
            },
            employment: (form.employment_type || form.start_date) ? {
                employment_type: form.employment_type || 'full_time',
                start_date: form.start_date || null,
            } : null,
            compensation: {
                pay_type: form.pay_type || 'salary',
                base_amount: parseFloat(form.base_amount) || 0,
                pay_frequency: form.pay_frequency || 'monthly',
            },
            access: {
                is_cashier: !!form.is_cashier,
                pin_code: form.pin || null,
                // POS privilege level — the POS manager gate reads the JWT role
                // claim minted from system_role, NOT the free-text job role
                // above. Omitting it leaves the employee on 'staff', unable to
                // configure a station.
                system_role: form.system_role || 'staff',
            },
        };

        try {
            await employeeV2Service.createEmployee(payload);
            setSaving(false);
            onSaved();
        } catch (err) {
            setSaving(false);
            const msg = err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed');
            setServerError(msg);
        }
    };

    const error = localError || '';

    return (
        <div
            className="emp-sheet-backdrop emp-backdrop-enter"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <GlassCard
                veil
                className="emp-sheet emp-sheet-enter"
                data-testid="create-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={t('add_employee', {}, 'Add Employee')}
            >
                <div className="emp-sheet-title">
                    {t('add_employee', {}, 'Add Employee')}
                </div>

                {error && (
                    <div className="emp-error" data-testid="create-error">{error}</div>
                )}
                {serverError && (
                    <div className="emp-error" data-testid="create-server-error">{serverError}</div>
                )}

                {/* Name + Role */}
                <div className="emp-grid-2">
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('name', {}, 'Name')}
                            {' '}*
                        </label>
                        <input
                            className="emp-field-input"
                            data-testid="create-name"
                            type="text"
                            value={form.name}
                            placeholder={t('full_name', {}, 'Full name')}
                            onChange={(e) => set('name', e.target.value)}
                        />
                    </div>
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('role', {}, 'Role')}
                        </label>
                        <input
                            className="emp-field-input"
                            data-testid="create-role"
                            type="text"
                            value={form.role}
                            placeholder={t('role_placeholder', {}, 'e.g. Cashier')}
                            onChange={(e) => set('role', e.target.value)}
                        />
                    </div>
                </div>

                {/* Department + Employment type */}
                <div className="emp-grid-2">
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('department', {}, 'Department')}
                        </label>
                        <select
                            className="emp-field-input emp-field-select"
                            data-testid="create-department"
                            value={form.department}
                            onChange={(e) => set('department', e.target.value)}
                        >
                            <option value="">{t('select_department', {}, 'Select department')}</option>
                            {DEPARTMENT_OPTIONS.map((d) => (
                                <option key={d.value} value={d.value}>{t(d.labelKey, {}, d.fallback)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('employment_type', {}, 'Employment Type')}
                        </label>
                        <select
                            className="emp-field-input emp-field-select"
                            data-testid="create-employment-type"
                            value={form.employment_type}
                            onChange={(e) => set('employment_type', e.target.value)}
                        >
                            <option value="">{t('select_type', {}, 'Select type')}</option>
                            {EMPLOYMENT_TYPES.map((et) => (
                                <option key={et.value} value={et.value}>
                                    {t(et.labelKey, {}, et.fallback)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Start date + Phone */}
                <div className="emp-grid-2">
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('start_date', {}, 'Start Date')}
                        </label>
                        <input
                            className="emp-field-input"
                            data-testid="create-start-date"
                            type="date"
                            value={form.start_date}
                            onChange={(e) => set('start_date', e.target.value)}
                        />
                    </div>
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('phone', {}, 'Phone')}
                        </label>
                        <input
                            className="emp-field-input"
                            data-testid="create-phone"
                            type="tel"
                            value={form.phone}
                            placeholder="+44 7700 000000"
                            onChange={(e) => set('phone', e.target.value)}
                        />
                    </div>
                </div>

                {/* Email */}
                <div className="emp-field">
                    <label className="emp-field-label">
                        {t('email', {}, 'Email')}
                    </label>
                    <input
                        className="emp-field-input"
                        data-testid="create-email"
                        type="email"
                        value={form.email}
                        placeholder="name@example.com"
                        onChange={(e) => set('email', e.target.value)}
                    />
                </div>

                {/* Pay type + Amount + Frequency */}
                <div className="emp-grid-2">
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('pay_type', {}, 'Pay Type')}
                        </label>
                        <select
                            className="emp-field-input emp-field-select"
                            data-testid="create-pay-type"
                            value={form.pay_type}
                            onChange={(e) => set('pay_type', e.target.value)}
                        >
                            {PAY_TYPES.map((pt) => (
                                <option key={pt.value} value={pt.value}>
                                    {t(pt.labelKey, {}, pt.fallback)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="emp-field">
                        <label className="emp-field-label">
                            {t('base_amount', {}, 'Base Amount')}
                        </label>
                        <input
                            className="emp-field-input emp-num"
                            data-testid="create-base-amount"
                            type="number"
                            value={form.base_amount}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            onChange={(e) => set('base_amount', e.target.value)}
                        />
                    </div>
                </div>

                <div className="emp-field">
                    <label className="emp-field-label">
                        {t('pay_frequency', {}, 'Pay Frequency')}
                    </label>
                    <select
                        className="emp-field-input emp-field-select"
                        data-testid="create-pay-frequency"
                        value={form.pay_frequency}
                        onChange={(e) => set('pay_frequency', e.target.value)}
                    >
                        {PAY_FREQUENCIES.map((pf) => (
                            <option key={pf.value} value={pf.value}>
                                {t(pf.labelKey, {}, pf.fallback)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Cashier toggle */}
                <div className="emp-toggle-row">
                    <span className="emp-toggle-label">
                        {t('is_cashier', {}, 'Cashier Access')}
                    </span>
                    <label className="emp-toggle" aria-label={t('is_cashier', {}, 'Cashier Access')}>
                        <input
                            type="checkbox"
                            data-testid="create-is-cashier"
                            checked={form.is_cashier}
                            onChange={(e) => set('is_cashier', e.target.checked)}
                        />
                        <span className="emp-toggle-track" />
                        <span className="emp-toggle-thumb" />
                    </label>
                </div>

                {/* PIN */}
                <div className="emp-field">
                    <label className="emp-field-label">
                        {t('pin_code', {}, 'PIN Code')}
                    </label>
                    <input
                        className="emp-field-input emp-num"
                        data-testid="create-pin"
                        type="password"
                        inputMode="numeric"
                        value={form.pin}
                        placeholder="••••••"
                        maxLength={6}
                        onChange={(e) => set('pin', e.target.value)}
                    />
                </div>

                {/* POS access level */}
                <div className="emp-field">
                    <label className="emp-field-label">
                        {t('pos_access_level', {}, 'POS access level')}
                    </label>
                    <select
                        className="emp-field-input emp-field-select"
                        data-testid="create-system-role"
                        value={form.system_role}
                        onChange={(e) => set('system_role', e.target.value)}
                    >
                        <option value="staff">{t('pos_access_staff', {}, 'Staff')}</option>
                        <option value="manager">{t('pos_access_manager', {}, 'Manager')}</option>
                    </select>
                    <div className="emp-field-hint">
                        {form.system_role === 'manager'
                            ? t('pos_access_manager_hint', {}, 'Can also configure the device and use manager functions.')
                            : t('pos_access_staff_hint', {}, 'Can take orders and payments on the till.')}
                    </div>
                </div>

                {/* Actions */}
                <div className="emp-actions">
                    <button
                        type="button"
                        className="emp-btn-cancel"
                        data-testid="create-cancel"
                        onClick={onClose}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="emp-btn-save"
                        data-testid="create-submit"
                        disabled={saving}
                        onClick={handleSubmit}
                    >
                        {saving && (
                            <span className="emp-spinner" aria-hidden="true" />
                        )}
                        {saving
                            ? t('saving', {}, 'Saving…')
                            : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Employee row — CondensedRow. See file header for the field mapping   */
/* and the pay/cashier disposition.                                     */
/* ------------------------------------------------------------------ */
function EmployeeRow({ emp, onPress, t }) {
    const isActive = (emp.status || '').toLowerCase() === 'active';
    const statusLabel = t(isActive ? 'active' : 'inactive', {}, isActive ? 'Active' : 'Inactive');
    const cashierText = emp.access?.is_cashier ? t('cashier', {}, 'Cashier') : null;
    const secondary = [emp.role, emp.department, cashierText].filter(Boolean).join(' · ');

    return (
        <CondensedRow
            icon={initials(emp.name)}
            tone={avatarTone(emp.name)}
            name={emp.name}
            secondary={secondary || undefined}
            pill={{ label: statusLabel, tone: statusTone(emp.status) }}
            onPress={onPress}
            testId={emp.employee_id}
        />
    );
}

/* ------------------------------------------------------------------ */
/* EmployeesScreen                                                     */
/* ------------------------------------------------------------------ */
export default function EmployeesScreen() {
    ensureCss();
    ensureKitCss();
    const { t, isRTL } = useLanguage();
    const { push } = useNav();
    useNavChrome(null, []);

    const [employees, setEmployees] = useState([]);
    const [status, setStatus] = useState('loading'); // loading | error | empty | ready
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState(null);
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null); // null=All | 'Active' | 'Inactive'
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [showCreate, setShowCreate] = useState(false);

    const fetchRef = useRef(0);

    const load = useCallback(async () => {
        const id = ++fetchRef.current;
        setStatus('loading');
        try {
            const data = await employeeV2Service.listEmployees();
            if (id !== fetchRef.current) return;
            setEmployees(Array.isArray(data) ? data : []);
            setStatus(Array.isArray(data) && data.length > 0 ? 'ready' : 'empty');
        } catch {
            if (id !== fetchRef.current) return;
            setStatus('error');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Derived: filtered roster (search + role/department/status) ──────
    const q = search.toLowerCase().trim();
    const filtered = employees.filter((emp) => {
        if (statusFilter && emp.status !== statusFilter) return false;
        if (roleFilter && emp.role !== roleFilter) return false;
        if (departmentFilter && emp.department !== departmentFilter) return false;
        if (q) {
            const nameMatch = (emp.name || '').toLowerCase().includes(q);
            const roleMatch = (emp.role || '').toLowerCase().includes(q);
            if (!nameMatch && !roleMatch) return false;
        }
        return true;
    });

    // A filter/search that matches nobody genuinely flips SurfaceStates into
    // its empty branch — this derivation is unchanged from pre-migration.
    const surfaceStatus = status === 'ready' && filtered.length === 0 ? 'empty' : status;

    // ── Derived: hero — same denominator as the rows rendered below it ──
    const heroCount = filtered.length;
    const heroActiveCount = filtered.filter((e) => (e.status || '').toLowerCase() === 'active').length;

    // ── Derived: FilterSheet groups (role/department from the roster,   ──
    //    status is a fixed two-value domain) ──────────────────────────────
    const roleOptions = useMemo(() => {
        const set = new Set(employees.map((e) => e.role).filter(Boolean));
        return Array.from(set).sort().map((r) => ({ id: r, label: r }));
    }, [employees]);
    const departmentOptions = useMemo(() => {
        const set = new Set(employees.map((e) => e.department).filter(Boolean));
        return Array.from(set).sort().map((d) => ({ id: d, label: d }));
    }, [employees]);
    const statusOptions = [
        { id: 'Active', label: t('active', {}, 'Active') },
        { id: 'Inactive', label: t('inactive', {}, 'Inactive') },
    ];

    const filterGroups = useMemo(() => {
        const groups = [];
        if (roleOptions.length > 0) groups.push({ id: 'role', label: t('role', {}, 'Role'), options: roleOptions });
        if (departmentOptions.length > 0) groups.push({ id: 'department', label: t('department', {}, 'Department'), options: departmentOptions });
        groups.push({ id: 'status', label: t('status', {}, 'Status'), options: statusOptions });
        return groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleOptions, departmentOptions, t]);

    const filterValue = { role: roleFilter, department: departmentFilter, status: statusFilter };
    const activeFilterCount = [roleFilter, departmentFilter, statusFilter].filter(Boolean).length;
    const activeFilterText = [roleFilter, departmentFilter, statusFilter].filter(Boolean).join(' · ');

    const handleFilterChange = (groupId, optionId) => {
        if (groupId === 'role') setRoleFilter(optionId);
        else if (groupId === 'department') setDepartmentFilter(optionId);
        else if (groupId === 'status') setStatusFilter(optionId);
    };

    const clearAllFilters = () => {
        setRoleFilter(null);
        setDepartmentFilter(null);
        setStatusFilter(null);
        setSearch('');
    };

    // ── Create saved handler ───────────────────────────────────────────
    const handleCreated = useCallback(() => {
        setShowCreate(false);
        load();
    }, [load]);

    const ptrDisabled = showCreate || filterSheetOpen;

    return (
        <div
            className="emp-wrap"
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="employees-screen"
        >
            {/* Hero card — headcount for the filtered roster, active count as
                caption. OUTSIDE SurfaceStates so it never vanishes. No
                AvatarRow — see file header: the list endpoint carries no
                on-shift signal, and adding a fetch to feed the hero would
                repeat the Task 6 regression. */}
            <GlassCard veil className="emp-hero" data-testid="employees-hero">
                <HeroStat
                    label={t('headcount', {}, 'Headcount')}
                    value={heroCount}
                    caption={t('n_active_employees', { count: heroActiveCount }, '{count} active')}
                />
            </GlassCard>

            {/* Control row — FilterButton only; no RangeBar equivalent. */}
            <div className="emp-control-row">
                <FilterButton
                    activeCount={activeFilterCount}
                    onPress={() => setFilterSheetOpen(true)}
                />
            </div>

            {/* Active-filter summary — OUTSIDE SurfaceStates so a filtered-to-
                empty result still shows the means to clear the filter. */}
            {activeFilterCount > 0 && (
                <div className="emp-filtersum">
                    <span className="emp-filtersum-text">{activeFilterText}</span>
                    <button
                        type="button"
                        className="emp-filtersum-clear"
                        data-testid="clear-filters"
                        onClick={clearAllFilters}
                    >
                        {t('clear', {}, 'Clear')}
                    </button>
                </div>
            )}

            {/* Search — OUTSIDE SurfaceStates, alongside the other controls: a
                term matching nobody flips status to 'empty', and a field
                living inside SurfaceStates would unmount mid-keystroke. */}
            <div className="emp-search-wrap">
                <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder={t('search_employees', {}, 'Search by name or role')}
                    debounce={0}
                />
            </div>

            <PullToRefresh onRefresh={load} disabled={ptrDisabled}>
                <SurfaceStates
                    status={surfaceStatus}
                    onRetry={load}
                    emptyTitle={
                        q || activeFilterCount > 0
                            ? t('no_employees_match', {}, 'No employees match filters')
                            : t('no_employees_yet', {}, 'No employees yet')
                    }
                    emptyMsg={
                        q || activeFilterCount > 0
                            ? t('try_adjusting_search_or_filter', {}, 'Try adjusting your search or filter')
                            : t('add_first_team_member', {}, 'Add your first team member')
                    }
                >
                    {/* Count line */}
                    <div className="emp-count-line" data-testid="emp-count-line">
                        {t('n_employees', { count: filtered.length }, '{count} Employees')}
                    </div>

                    {/* Employee list */}
                    <div className="emp-list" data-testid="emp-list">
                        {filtered.map((emp) => (
                            <EmployeeRow
                                key={emp.employee_id}
                                emp={emp}
                                onPress={() => push('employee-detail', {
                                    employeeId: emp.employee_id,
                                    name: emp.name,
                                })}
                                t={t}
                            />
                        ))}
                    </div>
                </SurfaceStates>
            </PullToRefresh>

            {/* FAB */}
            <button
                type="button"
                className="emp-fab"
                data-testid="add-employee-fab"
                aria-label={t('add_employee', {}, 'Add Employee')}
                onClick={() => setShowCreate(true)}
            >
                <PlusIcon />
                {t('add_employee', {}, 'Add Employee')}
            </button>

            {/* Create sheet */}
            <CreateEmployeeSheet
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onSaved={handleCreated}
                t={t}
            />

            {/* Filter sheet — role / department / status */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={filterGroups}
                value={filterValue}
                onChange={handleFilterChange}
                onClear={clearAllFilters}
            />
        </div>
    );
}
